import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  User,
  UserManager,
  WebStorageStateStore,
  type UserManagerSettings,
} from "oidc-client-ts";
import { useConfig } from "@/composables/config";

const { config } = useConfig();

interface AppUser {
  username: string;
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
}

const getOidcConfig = (): UserManagerSettings => ({
  authority: config.value.OidcAuthority!,
  metadata: {
    authorization_endpoint: `https://${config.value.OidcDomain}/authorize?audience=${config.value.OidcAuthority}`,
    userinfo_endpoint: `https://${config.value.OidcDomain}/userinfo`,
    issuer: config.value.OidcAuthority,
    jwks_uri: `https://${config.value.OidcDomain}/.well-known/jwks.json`,
    end_session_endpoint: `https://${config.value.OidcDomain}/v2/logout?client_id=${config.value.OidcClientId}&returnTo=${window.location.origin}`,
    token_endpoint: `https://${config.value.OidcDomain}/oauth/token`,
  },
  client_id: config.value.OidcClientId!,
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid profile email",
  loadUserInfo: true,
  monitorSession: true,
  automaticSilentRenew: true,
  silentRequestTimeoutInSeconds: 10,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

export const useAuthStore = defineStore("auth", () => {
  const userManager = new UserManager(getOidcConfig());
  const user = ref<AppUser | null>(null);
  const isInitialized = ref(false);
  const isUnknownUser = computed(() => !user.value || !user.value.userId);
  const isProcessingCallback = ref(false);

  async function init() {
    // Prevent multiple simultaneous initializations
    if (isInitialized.value || isProcessingCallback.value) {
      return;
    }

    try {
      // Check for authentication callback
      if (window.location.pathname === "/callback") {
        isProcessingCallback.value = true;
        try {
          await handleCallback();
          isInitialized.value = true;
        } catch (err) {
          console.error("Error handling callback:", err);
          // Clear any partial state and redirect to login
          await userManager.removeUser();
          await userManager.signinRedirect();
        } finally {
          isProcessingCallback.value = false;
        }
        return;
      }

      const oidcUser = await userManager.getUser();
      if (oidcUser?.expired === false) {
        await handleSignedInUser(oidcUser);
      } else {
        // No valid session, redirect to login
        await userManager.signinRedirect();
        return;
      }

      // Setup session monitoring (only once)
      if (!isInitialized.value) {
        userManager.events.addUserLoaded((user) => handleSignedInUser(user));
        userManager.events.addUserUnloaded(() => window.location.reload());
        userManager.events.addSilentRenewError((error) => void handleSilentRenewError(error));
      }

      isInitialized.value = true;
    } catch (error) {
      console.error("Failed to initialize auth:", error);
      throw new Error("Authentication failed");
    }
  }

  /**
   * A failed background renew used to reload the page, which loops while offline.
   * Offline: retry once the network is back. Online: retry once, then log in again.
   */
  async function handleSilentRenewError(error: Error) {
    console.warn("Silent token renew failed", error);
    if (!navigator.onLine) {
      window.addEventListener("online", () => void refreshToken(), { once: true });
      return;
    }
    await refreshToken();
  }

  async function refreshToken() {
    try {
      // Silent renew fetches a new token (with updated claims) without a redirect.
      // The current user stays stored until then, so a failure doesn't log the user out.
      const newUser = await userManager.signinSilent();

      if (newUser) {
        // Handle the new user data with updated claims
        await handleSignedInUser(newUser);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      if (!navigator.onLine) {
        // Keep the session; the next renew or request retries once we're online.
        return false;
      }
      // The session at the identity provider is gone, log in again
      await userManager.signinRedirect();
      return false;
    }
  }

  async function handleSignedInUser(oidcUser: User) {
    const userId = oidcUser.profile.hasOwnProperty(
      config.value.OidcUserIdClaimName
    )
      ? (oidcUser.profile[config.value.OidcUserIdClaimName] as string)
      : undefined;
    user.value = {
      username: oidcUser.profile.preferred_username || oidcUser.profile.sub,
      id: oidcUser.profile.sub,
      email: oidcUser.profile.email,
      firstName: oidcUser.profile.given_name,
      lastName: oidcUser.profile.family_name,
      userId,
    };
  }

  async function handleCallback() {
    try {
      // Get the full callback URL including query parameters
      const url = window.location.href;

      const oidcUser = await userManager.signinCallback(url);
      if (!oidcUser) {
        throw new Error("No OIDC user returned from callback");
      }
      await handleSignedInUser(oidcUser);

      // Use history.replaceState to avoid browser back button issues
      window.history.replaceState({}, document.title, "/");

      // Force a navigation to home
      window.location.href = "/";
    } catch (error) {
      console.error("Login callback failed:", error);
      throw error;
    }
  }

  function getToken() {
    return userManager.getUser().then((user) => user?.access_token);
  }

  async function logout() {
    try {
      await userManager.signoutRedirect();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }

  return {
    user,
    isInitialized,
    isUnknownUser,

    refreshToken,
    init,
    getToken,
    logout,
  };
});
